import PropTypes from 'prop-types';
import React from 'react';
import { useTheme } from 'styled-components';
import { ActionIcon, inkA, tokens } from 'components/v3';

// −/+ number control instead of the browser's spinner (design: .pf-stepper):
// two ActionIcons around the value and its suffix. The value never leaves
// [min, max]; a button at its limit is disabled (ActionIcon has no
// disabled prop, so it sits in a disabled fieldset).

const Limit = ({ off, children }) => (
  <fieldset
    disabled={off}
    style={{
      border: 0,
      padding: 0,
      margin: 0,
      minWidth: 0,
      display: 'inline-flex',
      opacity: off ? 0.35 : 1,
    }}
  >
    {children}
  </fieldset>
);

Limit.propTypes = {
  off: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

export const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const Stepper = ({ label, value, min, max, suffix, onChange }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const v = clamp(Number(value) || min, min, max);
  const id = `fb-stepper-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        id={id}
        style={{ fontSize: 14, fontWeight: 500, color: t.sub, marginBottom: 8 }}
      >
        {label}
      </div>
      <div
        role="group"
        aria-labelledby={id}
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <Limit off={v <= min}>
          <ActionIcon
            icon="minus"
            title={`Fewer ${suffix}`}
            onClick={() => onChange(clamp(v - 1, min, max))}
          />
        </Limit>
        <div
          aria-live="polite"
          style={{
            flex: 1,
            textAlign: 'center',
            padding: '8px 6px',
            borderRadius: 8,
            border: '1px solid ' + t.border,
            background: inkA(theme, 0.04),
            fontSize: 14,
            fontWeight: 600,
            color: t.text,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {`${v} ${suffix}`}
        </div>
        <Limit off={v >= max}>
          <ActionIcon
            icon="plus"
            title={`More ${suffix}`}
            onClick={() => onChange(clamp(v + 1, min, max))}
          />
        </Limit>
      </div>
    </div>
  );
};

Stepper.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  suffix: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

Stepper.defaultProps = { value: undefined };

export default Stepper;
